<template>
  <div class="home" :class="{ 'is-mobile': isMobile }" ref="container">
    <div class="sections-wrapper" :style="wrapperStyle">
      <HeroSection
        ref="section0"
        :class="{ 'is-active': isSectionActive(0) }"
      />
      <PremiumSection
        ref="premiumSection"
        :items="premiumItems"
        :is-active="isSectionActive(1)"
        :class="{ 'is-active': isSectionActive(1) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <CommunitySection
        ref="communitySection"
        :is-active="isSectionActive(2)"
        :class="{ 'is-active': isSectionActive(2) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <SpecializationSection
        ref="specSection"
        :is-active="isSectionActive(3)"
        :class="{ 'is-active': isSectionActive(3) }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
      />
      <UnitSection
        ref="section4"
        :is-active="isSectionActive(4)"
        :class="{ 'is-active': isSectionActive(4) }"
      />
      <DirectionSection
        ref="section5"
        :is-active="isSectionActive(5)"
        :class="{ 'is-active': isSectionActive(5) }"
      />
    </div>

    <!-- 푸터: 데스크탑은 fixed, 모바일은 일반 흐름 -->
    <FooterSection :class="isMobile ? 'footer-flow' : 'footer-fixed'" />

    <!-- 왼쪽 섹션 프로그레스 바 (모바일에서 숨김) -->
    <SectionProgress
      v-if="!isMobile"
      :current-section="currentSection"
      @go-to="scrollToSection"
    />
  </div>
</template>

<script>
import HeroSection from '@/components/web/home-v1/HeroSection.vue'
import PremiumSection from '@/components/web/home-v1/PremiumSection.vue'
import CommunitySection from '@/components/web/home-v1/CommunitySection.vue'
import SpecializationSection from '@/components/web/home-v1/SpecializationSection.vue'
import UnitSection from '@/components/web/home-v1/UnitSection.vue'
import DirectionSection from '@/components/web/home-v1/DirectionSection.vue'
import FooterSection from '@/components/web/home-v1/FooterSection.vue'
import SectionProgress from '@/components/web/common/SectionProgress.vue'

export default {
  name: 'HomeView',
  components: {
    HeroSection,
    PremiumSection,
    CommunitySection,
    SpecializationSection,
    UnitSection,
    DirectionSection,
    FooterSection,
    SectionProgress
  },
  data() {
    return {
      isMobile: false,
      currentSection: 0,
      totalSections: 6,
      showFooter: false,
      isScrolling: false,
      touchStartY: 0,
      // 모바일 Intersection Observer로 활성화된 섹션 추적
      mobileActiveSections: new Set(),
      mobileObserver: null,
      premiumItems: [
        {
          eng: 'Location',
          title: '직주근접 신중심지',
          desc: '수원 당수지구 중심에 위치하여\n삼성전자 수원사업장, 호매실 신도시 등\n수원 서남부 핵심 생활권을 누리는\n직주 근접 프리미엄 입지',
          image: 'location'
        },
        {
          eng: 'Transportation',
          title: '사통팔달 교통요지',
          desc: '수인분당선 호매실역(예정), 신분당선 연장(예정),\n봉담과선교, 수원문산고속도로 등\n사통팔달 광역교통망의 중심 교통요지',
          image: 'transportation'
        },
        {
          eng: 'Infrastructure',
          title: '원스톱 생활인프라',
          desc: '당수초, 호매실중 등 도보 통학권 학세권 형성 및\n롯데몰 수원, 홈플러스, 호매실 상업지구 등\n풍부한 생활 인프라 인접',
          image: 'infrastructure'
        },
        {
          eng: 'Nature',
          title: '청정자연 조성',
          desc: '단지 인근 당수천, 당수공원,\n호매실 중앙공원을 품은 쾌적한 자연환경과\n대단지 공원형 조경으로 힐링 라이프 조성',
          image: 'nature'
        }
      ]
    }
  },
  computed: {
    wrapperStyle() {
      if (this.isMobile) {
        return {}
      }
      const base = this.currentSection * 100
      const footerOffset = this.showFooter ? 280 : 0
      return {
        transform: `translateY(-${base}vh) translateY(-${footerOffset}px)`
      }
    }
  },
  watch: {
    isMobile(newVal, oldVal) {
      if (newVal === oldVal) return
      const container = this.$refs.container
      if (!container) return

      if (newVal) {
        // Switched to mobile: remove fullpage scroll listeners, setup observer
        container.removeEventListener('wheel', this.handleWheel)
        container.removeEventListener('touchstart', this.handleTouchStart)
        container.removeEventListener('touchend', this.handleTouchEnd)
        this.currentSection = 0
        this.showFooter = false
        this.setupMobileObserver()
      } else {
        // Switched to desktop: attach fullpage scroll listeners, teardown observer
        this.teardownMobileObserver()
        container.addEventListener('wheel', this.handleWheel, { passive: false })
        container.addEventListener('touchstart', this.handleTouchStart, { passive: true })
        container.addEventListener('touchend', this.handleTouchEnd, { passive: true })
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)

    if (this.isMobile) {
      // 모바일: Intersection Observer 기반 is-active 처리
      this.setupMobileObserver()
    } else if (this.$refs.container) {
      // 데스크탑: 풀페이지 스크롤 이벤트 등록
      this.$refs.container.addEventListener('wheel', this.handleWheel, { passive: false })
      this.$refs.container.addEventListener('touchstart', this.handleTouchStart, { passive: true })
      this.$refs.container.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    this.teardownMobileObserver()
    if (this.$refs.container) {
      this.$refs.container.removeEventListener('wheel', this.handleWheel)
      this.$refs.container.removeEventListener('touchstart', this.handleTouchStart)
      this.$refs.container.removeEventListener('touchend', this.handleTouchEnd)
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    /**
     * 섹션 활성화 여부 판단
     * - 데스크탑: currentSection 기반
     * - 모바일: Intersection Observer 기반 (한번 활성화되면 유지)
     */
    isSectionActive(index) {
      if (this.isMobile) {
        return this.mobileActiveSections.has(index)
      }
      return this.currentSection === index
    },

    /**
     * 모바일 Intersection Observer 설정
     * 섹션이 뷰포트에 20% 이상 보이면 is-active 적용 (한번 활성화되면 해제하지 않음)
     */
    setupMobileObserver() {
      this.teardownMobileObserver()

      // 첫 번째 섹션(히어로)은 즉시 활성화
      this.mobileActiveSections = new Set([0])

      this.mobileObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = parseInt(entry.target.dataset.sectionIndex, 10)
              if (!isNaN(index) && !this.mobileActiveSections.has(index)) {
                this.mobileActiveSections = new Set([...this.mobileActiveSections, index])
              }
            }
          })
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -10% 0px'
        }
      )

      // 각 섹션 요소에 data-section-index를 설정하고 observe
      this.$nextTick(() => {
        const sectionRefs = [
          this.$refs.section0,
          this.$refs.premiumSection,
          this.$refs.communitySection,
          this.$refs.specSection,
          this.$refs.section4,
          this.$refs.section5
        ]

        sectionRefs.forEach((ref, index) => {
          if (ref) {
            const el = ref.$el || ref
            el.dataset.sectionIndex = index
            this.mobileObserver.observe(el)
          }
        })
      })
    },

    /**
     * 모바일 Intersection Observer 해제
     */
    teardownMobileObserver() {
      if (this.mobileObserver) {
        this.mobileObserver.disconnect()
        this.mobileObserver = null
      }
    },

    handleWheel(e) {
      e.preventDefault()
      if (this.isScrolling) return

      const direction = e.deltaY > 0 ? 'down' : 'up'

      // 프리미엄 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 1) {
        const premiumSection = this.$refs.premiumSection
        if (premiumSection) {
          premiumSection.handleScroll(direction)
        }
        return
      }

      // 커뮤니티 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 2) {
        const communitySection = this.$refs.communitySection
        if (communitySection) {
          communitySection.handleScroll(direction)
        }
        return
      }

      // 스페셜 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 3) {
        const specSection = this.$refs.specSection
        if (specSection) {
          specSection.handleScroll(direction)
        }
        return
      }

      // 마지막 섹션에서 아래로 스크롤하면 푸터 드러냄
      if (e.deltaY > 0 && this.currentSection === this.totalSections - 1 && !this.showFooter) {
        this.showFooter = true
        this.isScrolling = true
        setTimeout(() => { this.isScrolling = false }, 800)
        return
      }

      // 푸터 보이는 상태에서 위로 스크롤하면 숨김
      if (e.deltaY < 0 && this.showFooter) {
        this.showFooter = false
        this.isScrolling = true
        setTimeout(() => { this.isScrolling = false }, 800)
        return
      }

      if (e.deltaY > 0 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (e.deltaY < 0 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY
    },
    handleTouchEnd(e) {
      if (this.isScrolling) return
      const touchEndY = e.changedTouches[0].clientY
      const diff = this.touchStartY - touchEndY
      const direction = diff > 0 ? 'down' : 'up'

      // 프리미엄 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 1) {
        const premiumSection = this.$refs.premiumSection
        if (premiumSection && Math.abs(diff) > 50) {
          premiumSection.handleScroll(direction)
        }
        return
      }

      // 커뮤니티 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 2) {
        const communitySection = this.$refs.communitySection
        if (communitySection && Math.abs(diff) > 50) {
          communitySection.handleScroll(direction)
        }
        return
      }

      // 스페셜 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 3) {
        const specSection = this.$refs.specSection
        if (specSection && Math.abs(diff) > 50) {
          specSection.handleScroll(direction)
        }
        return
      }

      if (diff > 50 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (diff < -50 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    scrollToSection(index) {
      this.isScrolling = true
      this.currentSection = index
      window.dispatchEvent(new CustomEvent('section-change', { detail: index }))

      setTimeout(() => {
        this.isScrolling = false
      }, 1800)
    },
    goToNextSection() {
      if (this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      }
    },
    goToPrevSection() {
      if (this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
  }
}
</script>

<style scoped>
.home {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.footer-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
}

.footer-flow {
  position: relative;
  z-index: 1;
}

.sections-wrapper {
  position: relative;
  z-index: 1;
  transition: transform 1.6s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

/* ==========================================
   Mobile: 일반 스크롤 모드 (768px 이하)
   ========================================== */
@media (max-width: 768px) {
  .home.is-mobile {
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .home.is-mobile .sections-wrapper {
    transform: none !important;
    transition: none;
    will-change: auto;
    overflow: hidden;
    max-width: 100vw;
  }

  /* 개별 섹션별로 제어 - 일괄 height: auto 제거 */
  /* 각 섹션 컴포넌트가 자체적으로 모바일 높이를 관리하도록 함 */

  /* 섹션 간 간격 */
  .home.is-mobile .sections-wrapper > *:not(:first-child) {
    margin-top: 20px;
  }

  /* MobileFloatingBar가 ~100px이므로 하단 여백 확보 */
  .home.is-mobile {
    padding-bottom: 110px;
  }
}
</style>
